/**
 * Main application file
 */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Buffer } from 'buffer';

import { FaCheckSquare } from "react-icons/fa"; // npm i react-icons #see https://github.com/react-icons/react-icons
import { HashRouter, Routes, useParams } from "react-router";
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { Route } = require('react-router'); // npm i react-router-dom #see https://github.com/ReactTraining/react-router

// App components importation (declare these files to webpack in `tsconfig.json`)
import { VERSION, DEBUG_MODE } from "./config";
import { readCookie, clearCookie, openLink, logError, isAuthorizationError } from "./parts/tools";
import { GotError } from './parts/alerts/gotError';
import { Loading } from './parts/alerts/loading';
import { Footer, Header } from "./parts/elements";
import { Auth } from './apps/myAccount/infosUser';
import { deleteCall, deleteRequest, getCall, getRequest, putRequest } from './parts/genericRequests';
import { Page404 } from './parts/dialogs/page404';
import { APropos } from './parts/dialogs/aPropos';
import { AuthDialog } from './parts/dialogs/auth';
import { AxiosError } from 'axios';
import { PRECONDITION_REQUIRED } from './parts/httpStatusCodes';

// Project app (lazy loading)
const Dashboard = React.lazy(() => import("./apps/dashboard"));
const MyAccount = React.lazy(() => import("./apps/myAccount"));
const ResetPasw = React.lazy(() => import("./apps/resetPasw"));
const CaseDetails = React.lazy(() => import("./apps/caseDetails"));

// Pages definition
const ct = 'Service Hub';
let i = 0;
const
    PAGE_HOME = i++, // 0
    PAGE_ACCOUNT = i++,
    PAGE_RESETPSW = i++,
    PAGE_TICKET = i++,
    PAGE_PROJECT = i;

const PAGES = [
    { id: PAGE_HOME, name: "home", newtab: false, title: 'Customer dashboard', document_title: "Home" },  // 0
    { id: PAGE_ACCOUNT, name: "account", newtab: false, title: 'My Account', document_title: 'Account' },
    { id: PAGE_RESETPSW, name: "resetpsw", newtab: false, title: 'Reset my password', document_title: 'Reset password' },
    { id: PAGE_TICKET, name: "ticket", newtab: true, title: 'Ticket details', document_title: 'Ticket details' },
    { id: PAGE_PROJECT, name: "project", newtab: true, title: 'Project details', document_title: 'Project details' },
];

type AppPropsType = {
    resetuuid?: any;
    page: any;
};

type AppStateType = {
    waitLogin: any;
    displayMode: any;
    resetUUID: any;
    pageInfo: any;
    auth: Auth;
    gridStates: any;
    serverError: any;
    error: any;
    accessList: any;
    ready: any;
    quit: any;
    authErrorCount: number;
};

/**
 * Represents the main application switch
 */
// eslint-disable-next-line no-restricted-syntax
class App extends React.Component<AppPropsType, AppStateType> {
    /**
     * Customized constructor
     * @param {any} props the global properties
     */
    private gridApps = ['approvals', 'projects', 'tickets']; // other grid apps here
    private initState: any;
    private dialogAuth: AuthDialog;

    constructor(props: { resetuuid: any; page: any; }) {
        super(props);

        const { resetuuid, page } = props;
        const pageInfo = getPageById(page);

        this.initState = {
            displayMode: page ?? PAGE_HOME,
            resetUUID: resetuuid,
            pageInfo: pageInfo
        };

        this.state = {
            ...this.initState,
            // auth from props or cookie
            auth: readCookie() ?? null,
            // search case type and grid states
            caseSearch: null,
            accessList: [],
            gridStates: this.gridApps.reduce((obj, e) => {
                obj[e] = {};
                return obj;
            }, {}),
            // general errors
            serverError: false,
            ready: false,
            quit: false,
            waitLogin: false,
            error: null,
            authErrorCount: 0
        };
    }

    /**
     * Render the component
     */
    render() {
        const {
            auth, accessList, displayMode, resetUUID, gridStates, serverError, ready, error, quit
        } = this.state;

        if (quit) return null; // on logout, set also reload the page

        if (serverError) { // client-version does not match server config version
            document.title = `Version error :: ${ct}`;
            return (
                <div>
                    <Header title={'Client version does NOT match server version'} />
                    <GotError />
                    <Footer />
                </div>
            );
        }

        if (displayMode === PAGE_RESETPSW && resetUUID) { // reset password request
            document.title = `Reset password :: ${ct}`;
            return (
                <React.Suspense fallback={<Loading />}>
                    <Header title={'Reset password'} />
                    <ResetPasw secret={resetUUID} error={(err) => this.errorHandler(err)} />
                </React.Suspense>
            );
        }

        if (auth === null) { // not connected > login dialog
            document.title = `Login :: ${ct}`;
            return (
                <div>
                    <Header title={'Please log in'} />
                    <AuthDialog ref={(c) => { this.dialogAuth = c; }} onValidAuth={(l, p, t, f) => this.checkLogin(l, p, t, f)}
                        onSentResetEmail={(e) => this.sendResetEmail(e)} error={error} />
                    <Footer />
                </div>
            );
        }

        if (!error && !ready) return <Loading />;

        const title = PAGES[displayMode].title;
        document.title = `${PAGES[displayMode].document_title} :: ${ct}`;

        // Fetch session storage
        let caseData = null;
        if ([PAGE_TICKET, PAGE_PROJECT].includes(displayMode)) {
            caseData = sessionStorage.getItem("caseData");
            try {
                if (caseData) caseData = JSON.parse(caseData);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (_err) {
                window.alert("Error: no valid case data found!");
            }
        }

        return (
            <div>
                <Header title={title} session={auth} onOpenPage={(p) => this.handleOpenPage(p)}
                    onLogout={() => this.httpLogout()} />

                {displayMode === PAGE_HOME &&
                    <React.Suspense fallback={<Loading />}>
                        <Dashboard auth={auth} error={(err) => this.errorHandler(err)} accessList={accessList}
                            gridStates={gridStates} onOpenCase={(p: string, c: any) => this.handleOpenCase(p, c)}
                            onRender={(s, a) => this.handleGridState(s, a)} />
                    </React.Suspense>}

                {displayMode === PAGE_ACCOUNT &&
                    <React.Suspense fallback={<Loading />}>
                        <MyAccount auth={auth} error={(err) => this.errorHandler(err)} />
                    </React.Suspense>}

                {[PAGE_TICKET, PAGE_PROJECT].includes(displayMode) &&
                    <React.Suspense fallback={<Loading />}>
                        <CaseDetails caseType={displayMode === PAGE_TICKET ? "Ticket" : "Project"} caseData={caseData}
                            error={(err: any) => this.errorHandler(err)} />
                    </React.Suspense>}
                <Footer />
            </div>
        );
    }

    /**
     * After mount components event
     */
    componentDidMount() {
        // server test
        this.httpTestServer(false);
    }

    /**
     * Authenticate to the web site to retrieve data
     *
     * Route: GET /api/login
     *
     * @param {string} login the user name for login
     * @param {string} passw the user password
     * @param {string} totp the OTP for 2-FA
     */
    private async httpAuthenticate(login: string, passw: string, totp: string) {
        return await getCall<Auth, Auth>("login", {
            headers: { // btoa
                'Authorization': "Basic " + Buffer.from(`${login}:${totp ? totp : ''}:${passw}`).toString('base64')
            }
        });
    }

    /**
     * Authenticate to the web site to retrieve data
     * @param {string} login the user name for login
     * @param {string} passw the user password
     * @param {function} next when the process is finished
     */
    private async checkLogin(login: string, passw: string, totp: string, next: () => void) {
        const { waitLogin } = this.state;
        if (waitLogin) {
            next();
            return;
        }

        await this.httpAuthenticate(login, passw, totp).then((auth) => {
            // writeTempVar('lastAuthTime', getTime());

            this.setState({ auth });

            if (!auth?.user_2fa) {
                if (confirm("2-Factor-Authentication is not enabled for your account.\n\nDo you want to enable it?")) {
                    return openLink('#/account');
                }
            }

            if (['', '#/', '#/home'].includes(window.location.hash))
                return this.httpTestServer(true);

        }).catch((err) => {
            // httpLogError(err, 'app.tsx:httpAuthenticate()');

            if (err.status === PRECONDITION_REQUIRED) {
                this.dialogAuth.promptTotp();
                return;
            }

            console.error(`authentication of '${login}' denied`, err);

            // setError({
            //     message: err.message,
            //     status: err.status
            // });

            this.setState({ waitLogin: true });
            setTimeout(() => this.setState({ waitLogin: false }), 2000);
        });

        next();
    }

    /**
     * Send password recovery email with reset link
     * @param {string} email the recipient email address
     */
    private sendResetEmail(email: string) {
        deleteCall(`sendresetemail/${encodeURIComponent(email)}`).catch(err => this.errorHandler(err));
    }

    /**
     * Logout in client
     */
    private logout() {
        this.setState({ ...this.initState, auth: null, quit: true });
        window.location.reload();
    }

    /**
     * Open an application page
     * @param pageName the page URL name
     */
    private handleOpenPage(pageName: string) {
        const pageInfo = getPageByName(pageName);
        if (pageInfo) {
            openLink(`#/${pageName}`, pageInfo.newtab);
            if (!pageInfo.newtab) this.setState({ displayMode: pageInfo.id, pageInfo: pageInfo });
        }
        else
            console.error(`Unknown page name: ${pageName}`);

    }

    /**
     * Open a case details view
     * @param {string} caseType the page name (case type 'ticket' or 'project')
     * @param {any} caseData the case type to open (ticket or project)
     */
    private handleOpenCase(caseType: string, caseData: any) {
        sessionStorage.setItem("caseData", JSON.stringify(caseData));
        openLink(`#/${caseType}`, true);
    }

    /**
     * Save the grid state
     * @param {string} app the app name
     * @param {any} gstate the new grid state
     */
    private handleGridState(app: string, gstate: any) {
        if (DEBUG_MODE) console.info(`Applying new grid state for app ${app}: ${gstate}`);
        const { gridStates } = this.state;
        gridStates[app] = gstate;
        this.httpSaveSetting('LAST_STATE_' + app, gstate && JSON.stringify(gstate));
    }

    /**
     * HTTP Test server availability
     */
    private httpTestServer(login: boolean) {
        if (login) {
            getRequest("version").then((response) => {
                if (!!response.data.client && (response.data.client !== VERSION)) {
                    // console.debug(response.data.client, VERSION);
                    this.setState({ serverError: true });
                }
                // cascade reading cases list then session then cases list then grid states
                // if (this.state.auth)
                this.httpGetSession(login);
            }).catch(() => this.setState({ serverError: true }));
        } else
            //  if (this.state.auth)
            this.httpGetSession(login);
    }

    /**
     * HTTP Get session information from API
     */
    private httpGetSession(login: boolean) {
        const { auth } = this.state;
        getRequest("session/get").then((response) => {
            // handle success
            const data = response.data;
            const tmp = Object.assign({}, auth);
            const newauth = Object.assign(tmp, data);
            // Object.keys(data).forEach((e) => auth[e] = data[e]);
            this.setState({ auth: newauth, error: null }, () => {
                this.httpLoadAllowed();
                this.httpLoadLastGridStates();
                if (login && ['', '#/'].includes(window.location.hash)) {
                    if (auth.extern && !auth.user_2fa) this.handleOpenPage("account");
                    else this.handleOpenPage("home");
                }
            });
        }).catch((err) => this.errorHandler(err, login ? undefined : "autologin"));
    }

    /**
     * Get alowed functions list
     */
    private httpLoadAllowed() {
        getRequest("access").then((response) => {
            this.setState({ accessList: response.data });
        }).catch((err) => this.errorHandler(err));
    }

    /**
     * List all last grid states
     */
    private httpLoadLastGridStates() {
        const { gridStates } = this.state;
        getCall<{ TypeName: string; StringValue: string; }[]>("filters/last").then((response) => {
            response.data.forEach((a) => {
                if (a.TypeName.startsWith('LAST_STATE_')) {
                    const app = a.TypeName.split('_').pop();
                    if (this.gridApps.includes(app)) gridStates[app] = JSON.parse(a.StringValue);
                }
            });

            this.setState({ gridStates, ready: true }); // last list set ready to true
        }).catch((err) => this.errorHandler(err));
    }

    /**
     * Save an user setting
     */
    private httpSaveSetting(name: string, value: string = null) {
        putRequest("users/setting", { name, value }).catch(err => this.errorHandler(err));
    }

    /**
     * Logout to server
     */
    private httpLogout() {
        deleteRequest("logout")
            .then(() => this.logout())
            .catch(err => this.errorHandler(err, "autologin"));
    }

    /**
     * Report an error
     * @param {any} err the error object
     */
    private errorHandler(err: AxiosError, identifier = undefined) {
        logError(err, (identifier ?? 'app.tsx:error()'));
        console.error(err);
        if (isAuthorizationError(err)) {
            clearCookie();
            this.setState({
                auth: null,
                error: identifier === "autologin" ? null : {
                    message: '❌ Access denied: authentication error or expired session',
                    status: err.status,
                    cause: err.message
                }
            });
        } else {
            const res = err.response;
            this.setState({
                error: {
                    message: res?.data,
                    status: res?.status,
                    cause: err.message
                }
            });
        }
    }
}

/**
 * Returns page information for an id
 * @param id
 * @returns Page information
 */
function getPageById(id: number) {
    return PAGES.find(obj => {
        return obj.id === id;
    });
}

/**
 * Returns page id for a name
 * @param name
 * @returns Page id
 */
function getPageByName(name: string) {
    return PAGES.find((obj) => obj.name === name);
}

function AppResetPsw() {
    const { secret } = useParams();
    return <App page={PAGE_RESETPSW} resetuuid={secret} />;
}

/**
 * Represents a wrapped "About" dialog
 */
function AppAbout() {
    const [auth, setAuth] = useState<{ extern: any; user_2fa: any; }>(null);

    useEffect(() => {
        const cookieAuth = readCookie();
        if (!cookieAuth) return;

        getRequest("session/get").then((data) => {
            // Object.keys(data).forEach((e) => auth[e] = data[e]);
            setAuth(Object.assign(cookieAuth ?? {}, data));
        }).catch((err) => console.error(err));
    }, []);

    document.title = `About :: ${ct}`;
    return (
        <div>
            <Header />
            <APropos auth={auth} />
        </div>
    );
}

/**
 * Represents a "Test" dialog
 */
function AppTest({ id }: { id?: string; }) {
    document.title = `Tests :: ${ct}`;
    if (!DEBUG_MODE) {
        return (
            <div>
                <Header /><FaCheckSquare /> No more tests to perform!
            </div>
        );
    }
    const result = "";
    // begin tests
    // end tests
    return (
        <div id={id}>
            <Header />
            {result}
        </div>
    );

    // /**
    //  * After mount components event
    //  */
    // componentDidMount() {
    //     this.pswDialog.show((p) => console.log(p));
    // }
}

/**
 * Main entry routing point
 */
// eslint-disable-next-line react/no-multi-comp
function AppRouter(): React.JSX.Element {
    return (
        <HashRouter>
            <React.Suspense fallback={<Loading />}>
                <Routes>
                    <Route exact path="/" element={<App page={PAGE_HOME} />} />
                    <Route exact path="/home" element={<App page={PAGE_HOME} />} />

                    <Route exact path="/ticket" element={<App page={PAGE_TICKET} />} />
                    <Route exact path="/project" element={<App page={PAGE_PROJECT} />} />

                    <Route exact path="/account" element={<App page={PAGE_ACCOUNT} />} />
                    <Route exact path="/resetpassword/:secret" element={<AppResetPsw />} />

                    <Route exact path="/tests" element={<AppTest />} />
                    <Route exact path="/about" element={<AppAbout />} />

                    <Route path="*" element={<Page404 />} />
                </Routes>
            </React.Suspense>
        </HashRouter>
    );
}

/*
 * Now we render all
 */
// eslint-disable-next-line adt-rules/no-document-get-by-in-react
ReactDOM.createRoot(document.getElementById('root')).render(<AppRouter />);
// Set the version label and clear loading icon

// eslint-disable-next-line adt-rules/no-document-get-by-in-react
document.getElementById("lbloading").style.display = "none";
